import { useEffect } from 'react';
import { Container, Stack, Typography } from '@mui/material';
import { logEvent } from 'firebase/analytics';

import PageTitle from '../../components/PageTitle/PageTitle';
import { RefundPolicyHelmet } from './MyHelmet';
import { analytics } from '../../config/firebase';

export default function RefundPolicy() {
  // Firebase analytics
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: 'Refund Policy',
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }, []);
  return (
    <>
      <RefundPolicyHelmet />
      <PageTitle>
        <Stack
          component={'header'}
          gap={1}
          justifyContent="center"
          textAlign={'center'}
        >
          <Typography variant="h1" sx={{ fontSize: '40px', fontWeight: '700' }}>
            Refund Policy
          </Typography>
          <Stack>
            <Typography
              variant="body1"
              sx={{
                fontSize: '18px',
                color: 'primary.neutral550',
              }}
            >
              Last Updated Mar 7th, 2024
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '14px',
                color: 'primary.neutral550',
              }}
            >
              This page doesn't have a translation yet. Stay tuned.
            </Typography>
          </Stack>
        </Stack>
      </PageTitle>
      <Container
        maxWidth="lg"
        sx={{
          pb: 4,
          pt: 2,
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        }}
      >
        <section>
          <strong>
            At Slex.tv, We are committed to ensuring your satisfaction with our
            products. If you are dissatisfied with your purchase or encounter an
            issue that we are unable to resolve, resulting in the system being
            unusable, we are happy to offer a full refund within 3 days of your
            original purchase.
          </strong>
          <p>
            We firmly believe in and stand behind our product 100%, but we
            understand that it may not meet everyone's expectations all of the
            time.
          </p>
          <p>A few conditions apply to our refund policy:</p>
          <ol>
            <li>
              <p>
                Technical Issues: We are unable to provide refunds for technical
                issues unless you allow us to assist you adequately. If we are
                unable to resolve the issue for you, we will gladly provide a
                refund. Please contact us before requesting a refund for
                technical issues.
              </p>
            </li>
            <li>
              <p>
                Timeframe: Refunds may only be issued within 3 days of the
                purchase date. After 3 days, no refunds can be processed.
              </p>
            </li>
          </ol>
          <p>
            Important Note: If you paid using a credit card, refunds are
            generally processed, and funds are returned to the debit card linked
            to your bank account within 20 business days. However, there may be
            cases where a refund takes longer to process, up to 90 days.
          </p>
          <p>
            Slex.tv will provide a full refund upon request via a support ticket
            if the request is submitted within 3 days of purchase. Once
            approved, you will receive a full refund of your purchase within 24
            hours. The refund will be issued to your bank account or through the
            same payment method you used for the purchase.
          </p>
          <p>
            Please note that we will only refund purchases made within the past
            3 days. For all other refund requests made beyond this period, the
            refund amount will be calculated based on the remaining unused
            subscription period. Our support team will contact users in all
            refund cases.
          </p>
          <p>
            If a customer changes their mind and wishes to switch between
            servers, they can still subscribe to other servers by purchasing a
            new subscription.
          </p>
          <p>
            We will not be able to provide any refunds or partial refunds in the
            following circumstances:
          </p>
          <ol>
            <li>
              <p>
                Channel Line-up or Performance: If a customer is not satisfied
                with the channel line-up or performance, we recommend trying our
                free trial before purchasing a subscription.
              </p>
            </li>
            <li>
              <p>
                Box Setup: If a customer is unable to set up their own box, we
                will provide support to guide them through the pre-defined
                configuration steps. However, our support is limited as we may
                not be aware of the specific circumstances with the box.
              </p>
            </li>
            <li>
              <p>
                Freezing or Non-Working Channels: Although our servers are
                generally stable, there may be temporary issues with channel
                freezing or non-functionality due to various reasons. In most
                cases, these issues are caused by factors such as customer
                internet fluctuations, box-related problems, or issues with the
                Internet Service Provider (ISP).
              </p>
            </li>
            <li>
              <p>
                Channel Outages: In the event that a channel is down from our
                server end, we will make every effort to resolve the issue
                promptly. However, please note that occasional channel outages
                may occur, and we cannot provide refunds solely based on the
                temporary unavailability of specific channels.
              </p>
            </li>
          </ol>
          <ol start="4">
            <li>
              <p>
                Customer Responsibility: It is the customer's responsibility to
                ensure that the TV services are compatible with their devices
                and meet their specific requirements before making a purchase.
                Refunds will not be granted if the customer fails to verify
                compatibility or suitability beforehand.
              </p>
            </li>
            <li>
              <p>
                Change of Mind: Refunds will not be granted if a customer simply
                changes their mind about the purchase or decides they no longer
                wish to use our services. We encourage customers to carefully
                consider their decision before making a purchase.
              </p>
            </li>
          </ol>
          <p>
            At Slex.tv, we strive to provide the best possible TV experience to
            our customers. While we make every effort to address any issues or
            concerns, we understand that there may be circumstances where a
            refund is necessary. Our support team is available to assist you in
            resolving any problems you may encounter.
          </p>
          <p>
            To request a refund, please submit a support ticket at{' '}
            <a href="mailto:contact@slex.tv">contact@slex.tv</a> within 3 days
            of your purchase, and our team will review your request and process
            the refund accordingly. We aim to provide a seamless refund process
            and ensure that you receive the refund in a timely manner.
          </p>
          <p>
            Thank you for choosing Slex.tv. We appreciate your trust in our
            brand and look forward to serving you with our high-quality TV
            services.
          </p>
        </section>
      </Container>
    </>
  );
}
